import { useEffect, useMemo, useRef, useState } from "react";
import { Group, Panel, Separator, type Layout } from "react-resizable-panels";
import { ChartWidget } from "./ChartWidget.tsx";
import { InstrumentsWidget } from "./InstrumentsWidget.tsx";
import { changeClass, formatMoney, formatNumber, formatPercent } from "./format.ts";
import { connectRealtime } from "./realtime.ts";
import { resetRealtimeQueue, useTerminalStore } from "./store.ts";
import {
  InstrumentCardWidget,
  OperationsWidget,
  OrderBookWidget,
  OrdersWidget,
  PortfolioWidget,
} from "./widgets.tsx";

const DEFAULT_LAYOUTS: Record<string, Layout> = {
  root: { instruments: 15, workspace: 85 },
  rows: { top: 53, bottom: 47 },
  top: { chart: 40, portfolio: 60 },
  bottom: { card: 25, orderbook: 20, orders: 27, operations: 28 },
};

function readLayout(key: string): Layout {
  const fallback = DEFAULT_LAYOUTS[key]!;
  try {
    const parsed = JSON.parse(localStorage.getItem(`terminal.layout.${key}`) ?? "null") as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return fallback;
    const candidate = parsed as Record<string, unknown>;
    if (!Object.keys(fallback).every((id) => typeof candidate[id] === "number")) return fallback;
    return candidate as Layout;
  } catch {
    return fallback;
  }
}

function saveLayout(key: string, layout: Layout): void {
  localStorage.setItem(`terminal.layout.${key}`, JSON.stringify(layout));
}

function ResizeHandle({ id }: { id: string }) {
  return <Separator className="resize-handle" id={id}><span /></Separator>;
}

function AccountSwitcher() {
  const bootstrap = useTerminalStore((state) => state.bootstrap);
  const selectAccount = useTerminalStore((state) => state.selectAccount);
  const switching = useTerminalStore((state) => state.switchingAccount);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const account = bootstrap?.account;
  const accounts = bootstrap?.accounts ?? [];

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="account-switcher" ref={rootRef}>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        className="account-select"
        disabled={switching || accounts.length < 2}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        type="button"
      >
        <span className={`broker-mark ${account?.broker ?? "tinvest"}`} aria-hidden="true" />
        <strong>{switching ? "Переключение…" : account?.name ?? "Брокерский счёт"}</strong>
        <span className="account-chevron" aria-hidden="true">⌄</span>
      </button>
      {open && (
        <div aria-label="Брокерские счета" className="account-menu" role="listbox">
          {accounts.map((candidate) => (
            <button
              aria-selected={candidate.key === account?.key}
              className={candidate.key === account?.key ? "selected" : ""}
              key={candidate.key}
              onClick={() => {
                setOpen(false);
                void selectAccount(candidate.key);
              }}
              role="option"
              type="button"
            >
              <span className={`broker-mark ${candidate.broker}`} aria-hidden="true" />
              <span><strong>{candidate.name}</strong><small>{{ finam: "Финам", tinvest: "Т‑Инвестиции", ibkr: "Interactive Brokers" }[candidate.broker]}</small></span>
              {candidate.key === account?.key && <i aria-hidden="true">✓</i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TopBar({ onLogout }: { onLogout: () => void }) {
  const bootstrap = useTerminalStore((state) => state.bootstrap);
  const portfolio = useTerminalStore((state) => state.portfolio);
  const status = useTerminalStore((state) => state.status);
  const indicators = bootstrap?.indicators ?? [];

  return (
    <header className="topbar">
      <div className="market-row">
        <div className="terminal-mark" aria-hidden="true">+</div>
        <div className="market-strip" aria-label="Рыночные индикаторы">
          {indicators.map((indicator) => (
            <div className="market-indicator" key={indicator.key}>
              <i aria-hidden="true" />
              <span>{indicator.label}</span>
              <strong>{formatNumber(indicator.price, 4)}</strong>
              <small className={changeClass(indicator.changePercent)}>{formatPercent(indicator.changePercent)}</small>
            </div>
          ))}
        </div>
      </div>
      <div className="account-row">
        <AccountSwitcher />
        <div className="account-metric"><strong>{formatMoney(portfolio?.totalAmount, portfolio?.currency)}</strong></div>
        <div className="account-metric"><span>Сегодня</span><strong className={changeClass(portfolio?.dailyYieldPercent)}>{formatMoney(portfolio?.dailyYield, portfolio?.currency)} · {formatPercent(portfolio?.dailyYieldPercent)}</strong></div>
        <div className="account-metric"><span>Доход</span><strong className={changeClass(portfolio?.expectedYieldPercent)}>{formatPercent(portfolio?.expectedYieldPercent)}</strong></div>
        <div className={`connection ${status.state}`} title={status.error}><i />{status.state}</div>
        <button className="logout-button" onClick={onLogout} title="Выйти" type="button">↗</button>
      </div>
    </header>
  );
}

function WorkspaceToolbar() {
  const bootstrap = useTerminalStore((state) => state.bootstrap);
  return (
    <div className="workspace-toolbar">
      <div className="workspace-tab active"><span>Терминал</span></div>
      <div className="workspace-toolbar-spacer" />
      {bootstrap?.brokerWarnings?.map(warning => <span className="broker-note" key={warning}>{warning}</span>)}
      {bootstrap?.account.broker === 'ibkr' && <span className="broker-note">IBKR: лучшие цены покупки/продажи · сделки за 7 дней</span>}
      {bootstrap?.ibkrLoginAvailable && <a className="broker-note" href="/ibkr/" target="_blank" rel="noreferrer">Войти в IBKR ↗</a>}
      <span className="readonly-badge">READ ONLY</span>
    </div>
  );
}

function Workspace() {
  const addWatchlistInstrument = useTerminalStore((state) => state.addWatchlistInstrument);
  const candles = useTerminalStore((state) => state.candles);
  const interval = useTerminalStore((state) => state.interval);
  const operations = useTerminalStore((state) => state.operations);
  const orderBook = useTerminalStore((state) => state.orderBook);
  const orders = useTerminalStore((state) => state.orders);
  const portfolio = useTerminalStore((state) => state.portfolio);
  const quotes = useTerminalStore((state) => state.quotes);
  const selected = useTerminalStore((state) => state.selectedInstrument);
  const select = useTerminalStore((state) => state.select);
  const removeWatchlistInstrument = useTerminalStore((state) => state.removeWatchlistInstrument);
  const watchlist = useTerminalStore((state) => state.watchlist);
  const layouts = useMemo(() => ({
    root: readLayout("root"),
    rows: readLayout("rows"),
    top: readLayout("top"),
    bottom: readLayout("bottom"),
  }), []);

  return (
    <div className="workspace" data-testid="terminal-workspace">
      <Group defaultLayout={layouts.root} id="root" onLayoutChanged={(layout) => saveLayout("root", layout)} orientation="horizontal">
        <Panel id="instruments" minSize={230}>
          <InstrumentsWidget
            instruments={watchlist}
            onAdd={addWatchlistInstrument}
            onRemove={removeWatchlistInstrument}
            onSelect={(uid) => void select(uid)}
            quotes={quotes}
            selectedUid={selected?.uid}
          />
        </Panel>
        <ResizeHandle id="root-handle" />
        <Panel id="workspace" minSize={1_450}>
          <Group defaultLayout={layouts.rows} id="rows" onLayoutChanged={(layout) => saveLayout("rows", layout)} orientation="vertical">
            <Panel id="top" minSize={390}>
              <Group defaultLayout={layouts.top} id="top" onLayoutChanged={(layout) => saveLayout("top", layout)} orientation="horizontal">
                <Panel id="chart" minSize={560}>
                  <ChartWidget
                    candles={candles}
                    instrumentUid={selected?.uid}
                    interval={interval}
                    onIntervalChange={(value) => selected && void select(selected.uid, value)}
                    ticker={selected?.ticker}
                  />
                </Panel>
                <ResizeHandle id="top-handle" />
                <Panel id="portfolio" minSize={700}><PortfolioWidget onSelect={(uid) => void select(uid)} portfolio={portfolio} selectedUid={selected?.uid} /></Panel>
              </Group>
            </Panel>
            <ResizeHandle id="rows-handle" />
            <Panel id="bottom" minSize={390}>
              <Group defaultLayout={layouts.bottom} id="bottom" onLayoutChanged={(layout) => saveLayout("bottom", layout)} orientation="horizontal">
                <Panel id="card" minSize={310}><InstrumentCardWidget instrument={selected} portfolio={portfolio} quote={selected ? quotes[selected.uid] : undefined} /></Panel>
                <ResizeHandle id="card-handle" />
                <Panel id="orderbook" minSize={240}>
                  <OrderBookWidget currency={selected?.currency} orderBook={orderBook} ticker={selected?.ticker} />
                </Panel>
                <ResizeHandle id="orderbook-handle" />
                <Panel id="orders" minSize={360}><OrdersWidget onSelect={(uid) => void select(uid)} orders={orders} selectedUid={selected?.uid} /></Panel>
                <ResizeHandle id="orders-handle" />
                <Panel id="operations" minSize={380}><OperationsWidget onSelect={(uid) => void select(uid)} operations={operations} selectedUid={selected?.uid} /></Panel>
              </Group>
            </Panel>
          </Group>
        </Panel>
      </Group>
    </div>
  );
}

export function Terminal({ onLogout }: { onLogout: () => void }) {
  const bootstrap = useTerminalStore((state) => state.bootstrap);
  const error = useTerminalStore((state) => state.error);
  const loadBootstrap = useTerminalStore((state) => state.loadBootstrap);
  const loading = useTerminalStore((state) => state.loading);

  useEffect(() => {
    void loadBootstrap();
    const disconnect = connectRealtime();
    return () => {
      disconnect();
      resetRealtimeQueue();
    };
  }, [loadBootstrap]);

  if (!bootstrap && !loading) {
    return (
      <main className="terminal-error">
        <span className="login-mark">T</span>
        <h1>Нет связи с брокером</h1>
        <p>{error ?? "Проверьте токены и доступность API"}</p>
        <button onClick={() => void loadBootstrap()} type="button">Повторить</button>
      </main>
    );
  }

  return (
    <main className="terminal-shell">
      <TopBar onLogout={onLogout} />
      <WorkspaceToolbar />
      <Workspace />
      {loading && !bootstrap && <div className="loading-overlay"><span className="spinner" />Загрузка данных…</div>}
      {error && bootstrap && <button className="error-toast" onClick={() => useTerminalStore.getState().setError()} type="button">{error}<span>×</span></button>}
    </main>
  );
}
